import React, { useEffect, useRef, useState } from 'react';

/**
 * RevealOnScroll
 * ------------------------------------------------------------
 * Wraps children in a div that fades + translates upward when
 * it scrolls into view. Triggers once, then unobserves.
 * Respects `prefers-reduced-motion: reduce` by skipping the
 * animation entirely (children render visible immediately).
 *
 * Props:
 *   delay: ms to wait before animating (for staggered groups)
 *   className: extra classes to merge onto the wrapper
 */
export default function RevealOnScroll({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setVisible(true);
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      className={`transition-all duration-700 ease-out will-change-transform ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
    >
      {children}
    </div>
  );
}
